import * as THREE from 'three';

/**
 * 骨骼动画管理器模块
 * 基于 AnimationMixer 管理数字人骨骼动画的播放、暂停、切换
 * 支持多动作混合过渡、动画剪辑列表查询
 */
export class AnimationManager {
  /** @type {THREE.AnimationMixer | null} */
  #mixer = null;
  /** @type {Map<string, THREE.AnimationClip>} */
  #clips = new Map();
  /** @type {THREE.AnimationAction | null} */
  #currentAction = null;
  /** 动画淡入淡出过渡时间（秒） */
  #fadeDuration = 0.3;

  /**
   * @param {THREE.Group | THREE.Object3D} model - 数字人模型实例
   */
  constructor(model) {
    this.model = model;
  }

  /**
   * 从动画剪辑数组中提取并注册到 Mixer
   * @param {THREE.AnimationClip[]} clips - GLTF 加载的动画剪辑数组
   */
  initAnimationsFromClips(clips) {
    this.#mixer = new THREE.AnimationMixer(this.model);

    if (clips && clips.length > 0) {
      for (const clip of clips) {
        this.#clips.set(clip.name, clip);
        console.log(`[AnimationManager] 已注册动画: ${clip.name} (${clip.duration.toFixed(2)}s)`);
      }
    } else {
      console.warn('[AnimationManager] 模型不包含动画剪辑');
    }
  }

  /**
   * 兼容旧接口：从模型场景中提取动画剪辑
   * @deprecated 使用 initAnimationsFromClips 替代
   */
  initAnimations(gltfScene) {
    this.initAnimationsFromClips(gltfScene.animations || []);
  }

  /**
   * 播放指定名称的动画
   * @param {string} name - 动画名称（需与模型内动画剪辑名称一致）
   * @param {object} [options]
   * @param {boolean} [options.loop=true] - 是否循环播放
   * @param {number} [options.timeScale=1] - 播放速率（1=正常，0.5=慢放，2=快进）
   * @param {boolean} [options.fadeIn=true] - 是否淡入
   * @returns {THREE.AnimationAction | null} 当前动画动作
   */
  play(name, options = {}) {
    const { loop = true, timeScale = 1, fadeIn = true } = options;

    const clip = this.#clips.get(name);
    if (!clip) {
      console.warn(`[AnimationManager] 未找到动画 "${name}"，可用动画: ${this.getAnimationNames().join(', ') || '无'}`);
      return null;
    }

    if (!this.#mixer) {
      console.warn('[AnimationManager] 动画 Mixer 未初始化');
      return null;
    }

    // 如果有当前动画，先淡出停止
    if (this.#currentAction && fadeIn) {
      this.#currentAction.fadeOut(this.#fadeDuration);
    }

    // 创建新的 AnimationAction
    const action = this.#mixer.clipAction(clip);
    action.reset();

    // 设置循环模式
    if (!loop) {
      action.setLoop(THREE.LoopOnce);
      action.clampWhenFinished = true;
    }

    action.timeScale = timeScale;

    // 淡入
    if (fadeIn) {
      action.fadeIn(this.#fadeDuration);
    }
    action.play();

    this.#currentAction = action;
    return action;
  }

  /** 暂停当前动画 */
  pause() {
    if (this.#currentAction) {
      this.#currentAction.paused = true;
    }
  }

  /** 恢复当前动画 */
  resume() {
    if (this.#currentAction) {
      this.#currentAction.paused = false;
    }
  }

  /** 停止当前动画 */
  stop() {
    if (this.#currentAction) {
      this.#currentAction.stop();
      this.#currentAction = null;
    }
  }

  /** 设置当前动画播放速率 */
  setTimeScale(scale) {
    if (this.#currentAction) {
      this.#currentAction.timeScale = scale;
    }
  }

  /**
   * 更新动画状态（每帧调用）
   * @param {number} delta - 距离上一帧的时间（秒）
   */
  update(delta) {
    this.#mixer?.update(delta);
  }

  /** 获取所有已注册的动画名称列表 */
  getAnimationNames() {
    return Array.from(this.#clips.keys());
  }

  /** 获取当前正在播放的动画名称 */
  getCurrentAnimationName() {
    return this.#currentAction?.getClip().name ?? null;
  }

  /** 获取所有动画的详细信息（名称、时长） */
  getAnimationDetails() {
    const details = [];
    for (const [name, clip] of this.#clips) {
      details.push({
        name,
        duration: Math.round(clip.duration * 100) / 100,
        tracks: clip.tracks.length,
      });
    }
    return details;
  }
}
